/**
 * Template de prise de besoin : Politique de classification des incidents v1
 * Aligné sur ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0
 * Définit la sévérité, l'impact, l'urgence et les niveaux de priorité
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePolitiqueClassificationIncidentsV1: IntakeTemplate = {
  id: 'politique-classification-incidents-v1',
  name: 'Prise de besoin Politique de classification des incidents v1',
  description:
    'Questionnaire complet pour élaborer une politique de classification des incidents conforme ITIL 4, COBIT 2019, ISO 27001 et NIST CSF 2.0. Définit la sévérité, l\'impact, l\'urgence et les niveaux de priorité (P1-P4 ITIL / CAT 1-5 NIST).',
  documentTypeCode: 'INCIDENT_CLASSIFICATION_POLICY',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Contexte & Système de Classification',
      description: 'Informations sur l\'entreprise et choix du système de classification',
      icon: 'Building2',
      questions: [
        {
          id: 'entreprise',
          label: 'Entreprise',
          type: 'text',
          placeholder: 'Nom de l\'entreprise',
          required: true,
          helpText: 'Identifiant principal pour la politique de classification des incidents.',
        },
        {
          id: 'systeme_classification',
          label: 'Quel système de classification souhaitez-vous utiliser ?',
          type: 'select',
          options: [
            'ITIL 4 (P1-P4)',
            'NIST CSF 2.0 (CAT 1-5)',
            'Système personnalisé',
            'Hybride (ITIL + NIST)',
            'À déterminer',
          ],
          required: true,
          helpText: 'ITIL 4 et NIST CSF 2.0: Choix du système de classification pour assurer la cohérence.',
        },
        {
          id: 'facteurs_classification',
          label: 'Quels facteurs doivent être utilisés pour classifier les incidents ?',
          type: 'multiselect',
          options: [
            'Impact business',
            'Urgence',
            'Sévérité',
            'Nombre d\'utilisateurs affectés',
            'Criticité du service',
            'Impact financier',
            'Impact sur la réputation',
            'Conformité réglementaire',
            'Autre',
          ],
          required: true,
          helpText: 'ITIL 4: Facteurs de classification pour déterminer la priorité des incidents.',
        },
        {
          id: 'ton_document_politique',
          label: 'Quel ton souhaitez-vous pour cette politique ?',
          type: 'select',
          options: [
            'Formel et juridique (ton impératif, sanctions claires)',
            'Pédagogique et collaboratif (ton explicatif, guide pratique)',
            'Mixte (équilibre entre formel et pédagogique)',
          ],
          helpText: 'Le ton influence la réception et l\'adhésion des parties prenantes.',
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Définition des Niveaux (ITIL P1-P4)',
      description: 'Définition détaillée des niveaux de priorité ITIL',
      icon: 'AlertTriangle',
      questions: [
        {
          id: 'criteres_p1',
          label: 'Quels sont les critères pour un incident P1 (Critique) ?',
          type: 'textarea',
          placeholder: 'Ex: Service critique complètement indisponible, impact business majeur, plus de 50 utilisateurs affectés...',
          helpText: 'ITIL 4: Définition claire des critères P1 pour classification rapide et précise.',
        },
        {
          id: 'criteres_p2',
          label: 'Quels sont les critères pour un incident P2 (Majeur) ?',
          type: 'textarea',
          placeholder: 'Ex: Service partiellement indisponible, impact business modéré, 10-50 utilisateurs affectés...',
          helpText: 'ITIL 4: Définition claire des critères P2.',
        },
        {
          id: 'criteres_p3',
          label: 'Quels sont les critères pour un incident P3 (Mineur) ?',
          type: 'textarea',
          placeholder: 'Ex: Dégradation de service mineure, impact business faible, moins de 10 utilisateurs affectés...',
          helpText: 'ITIL 4: Définition claire des critères P3.',
        },
        {
          id: 'criteres_p4',
          label: 'Quels sont les critères pour un incident P4 (Question) ?',
          type: 'textarea',
          placeholder: 'Ex: Question ou demande d\'information, pas d\'impact sur le service...',
          helpText: 'ITIL 4: Définition claire des critères P4.',
        },
        {
          id: 'delais_resolution_p1',
          label: 'Quels sont les délais cibles de résolution pour P1 (Critique) ?',
          type: 'text',
          placeholder: 'Ex: 1 heure pour résolution initiale, 4 heures pour résolution complète',
          helpText: 'ITIL 4: Délais cibles essentiels pour mesurer la performance.',
        },
        {
          id: 'delais_resolution_p2',
          label: 'Quels sont les délais cibles de résolution pour P2 (Majeur) ?',
          type: 'text',
          placeholder: 'Ex: 4 heures pour résolution initiale, 1 jour ouvrable pour résolution complète',
          helpText: 'ITIL 4: Délais cibles pour P2.',
        },
        {
          id: 'delais_resolution_p3',
          label: 'Quels sont les délais cibles de résolution pour P3 (Mineur) ?',
          type: 'text',
          placeholder: 'Ex: 1 jour ouvrable pour résolution initiale, 3 jours ouvrables pour résolution complète',
          helpText: 'ITIL 4: Délais cibles pour P3.',
        },
        {
          id: 'delais_resolution_p4',
          label: 'Quels sont les délais cibles de résolution pour P4 (Question) ?',
          type: 'text',
          placeholder: 'Ex: 3 jours ouvrables pour réponse',
          helpText: 'ITIL 4: Délais cibles pour P4.',
        },
      ],
    },
    {
      id: 'phase3',
      title: 'Définition des Niveaux (NIST CAT 1-5)',
      description: 'Définition détaillée des niveaux de catégorie NIST',
      icon: 'Shield',
      questions: [
        {
          id: 'utiliser_nist',
          label: 'Souhaitez-vous utiliser le système de classification NIST (CAT 1-5) ?',
          type: 'select',
          options: ['Oui', 'Non', 'En complément d\'ITIL'],
          helpText: 'NIST CSF 2.0: Système de classification alternatif ou complémentaire.',
        },
        {
          id: 'criteres_cat1',
          label: 'Quels sont les critères pour un incident CAT 1 (Critique) ?',
          type: 'textarea',
          placeholder: 'Ex: Impact systémique majeur, compromission de sécurité critique...',
          helpText: 'NIST CSF 2.0: Définition des critères CAT 1.',
        },
        {
          id: 'criteres_cat2',
          label: 'Quels sont les critères pour un incident CAT 2 (Élevé) ?',
          type: 'textarea',
          placeholder: 'Ex: Impact significatif, compromission de sécurité importante...',
          helpText: 'NIST CSF 2.0: Définition des critères CAT 2.',
        },
        {
          id: 'criteres_cat3',
          label: 'Quels sont les critères pour un incident CAT 3 (Modéré) ?',
          type: 'textarea',
          placeholder: 'Ex: Impact modéré, compromission de sécurité limitée...',
          helpText: 'NIST CSF 2.0: Définition des critères CAT 3.',
        },
        {
          id: 'criteres_cat4',
          label: 'Quels sont les critères pour un incident CAT 4 (Faible) ?',
          type: 'textarea',
          placeholder: 'Ex: Impact faible, compromission de sécurité mineure...',
          helpText: 'NIST CSF 2.0: Définition des critères CAT 4.',
        },
        {
          id: 'criteres_cat5',
          label: 'Quels sont les critères pour un incident CAT 5 (Informel) ?',
          type: 'textarea',
          placeholder: 'Ex: Pas d\'impact réel, information seulement...',
          helpText: 'NIST CSF 2.0: Définition des critères CAT 5.',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Matrice de Décision & Processus',
      description: 'Matrice de décision et processus de classification',
      icon: 'FileCheck',
      questions: [
        {
          id: 'matrice_decision',
          label: 'Souhaitez-vous inclure une matrice de décision pour la classification ?',
          type: 'select',
          options: ['Oui, matrice détaillée', 'Oui, matrice simple', 'Non', 'À déterminer'],
          helpText: 'ITIL 4: Matrice de décision utile pour classifier rapidement et de manière cohérente.',
        },
        {
          id: 'qui_classifie',
          label: 'Qui est responsable de classifier les incidents ?',
          type: 'select',
          options: [
            'Technicien de première ligne',
            'Gestionnaire d\'incidents',
            'Gestionnaire de service',
            'Technicien selon expertise',
            'Autre',
          ],
          helpText: 'ITIL 4: Attribution claire de la responsabilité de classification.',
        },
        {
          id: 'reclassification',
          label: 'Souhaitez-vous permettre la reclassification des incidents ?',
          type: 'select',
          options: ['Oui, avec approbation', 'Oui, librement', 'Non', 'À déterminer'],
          helpText: 'ITIL 4: Processus de reclassification pour ajuster la priorité si nécessaire.',
        },
        {
          id: 'formation_classification',
          label: 'Souhaitez-vous rendre la formation en classification obligatoire ?',
          type: 'select',
          options: ['Oui, pour tous', 'Oui, pour l\'équipe TI uniquement', 'Non, formation recommandée', 'À déterminer'],
          helpText: 'ITIL 4: Formation essentielle pour assurer une classification cohérente.',
        },
        {
          id: 'revision_politique',
          label: 'À quelle fréquence souhaitez-vous réviser cette politique ?',
          type: 'select',
          options: ['Annuelle', 'Semestrielle', 'Trimestrielle', 'Lors de changements majeurs', 'À déterminer'],
          helpText: 'ITIL 4: Révision régulière essentielle pour maintenir la pertinence de la politique.',
        },
      ],
    },
  ],
};





